import classNames from 'classnames';
import { ReactEventHandler, useCallback, useState } from 'react';
import { Badge, Button, Flex, Heading } from 'yoso';
import { FriendNftContent } from '~/components/Friend/Content/FriendNftContent';
import friendNftContentStyles from '~/components/Friend/Content/FriendNftContent.module.scss';
import { T } from '~/components/I18n';
import { Boost2D } from '~/components/Icon';
import cube from '~/components/LevelUp/Popup/Result/cube.gif';
import { styles as bottomSheetStyles } from '~/components/Modal/BottomSheet/BottomSheet';
import { formatBoost } from '~/components/Util/number';
import { EvolveToken, useEvolutionStatus } from '../Form/EvolveForm.hooks';
import styles from './EvolveResult.module.scss';

export type EvolveResultProps = {
  evolutionId: EvolveToken['evolutionId'];
  className?: string;
};

export const EvolveResult = ({ evolutionId, className }: EvolveResultProps) => {
  const [loaded, setLoaded] = useState(false);
  const { data } = useEvolutionStatus(evolutionId);
  const {
    contentsUrl = '',
    originContentsUrl = '',
    contentsType,
    bonusBoost = 0,
    boost = 0,
    evolvedGrowth = '',
    nftId,
  } = data || {};
  const onImageLoad: ReactEventHandler<HTMLImageElement> = useCallback((e) => {
    setLoaded((loaded) => {
      if (loaded) return loaded;
      const target = e.target as HTMLImageElement;
      // Force reanimate
      // eslint-disable-next-line no-self-assign
      target.src = target.src;
      return true;
    });
  }, []);
  return (
    <div
      className={classNames(
        styles.root,
        {
          [styles.animate]: loaded,
        },
        className
      )}>
      <div
        className={styles.background}
        // data-event="impression"
        // data-event-category="levelup_unpacking_popup"
      >
        <Heading level={4} className={classNames(styles.title, bottomSheetStyles.heading)}>
          <T name="citizen_membership_levelup_unpacking_popup_title" />
        </Heading>
        <img src={cube} className={styles.image} alt="Now Unpacking..." onLoad={onImageLoad} />
      </div>
      <div className={styles.foreground}>
        <Heading level={5} className={styles.title}>
          <T name="citizen_evolve_done_popup_title" />
        </Heading>
        <p className={styles.description}>
          <T
            name="citizen_evolve_done_popup_des1"
            options={[`${formatBoost(boost - bonusBoost)}%`]}
            pre={null}
          />
        </p>
        <Flex container className={styles.bonus} justifyContent="center">
          <span className={styles.bonusBadge}>
            <T
              name="citizen_evolve_done_popup_random"
              className={styles.random}
              options={[<strong key="boost">{formatBoost(bonusBoost)}% Boost</strong>]}
            />
          </span>
        </Flex>
        <FriendNftContent
          className={styles.content}
          originContentsUrl={originContentsUrl}
          contentsUrl={contentsUrl}
          contentsType={contentsType}
          boost={boost}
          badge={
            <Badge className={classNames(friendNftContentStyles.badge, styles.badge)}>
              <Boost2D />
              Boost {formatBoost(boost)}%
            </Badge>
          }
        />
        <p className={styles.name}>{evolvedGrowth}</p>
        <Button
          as="a"
          color="primary"
          className={classNames(classNames(styles.button, bottomSheetStyles.confirmButton))}
          href={`${import.meta.env.VITE_CITIZEN_STORE_URL}/nfts/${nftId}`}>
          <T name="citizen_button_title_see_nft" />
        </Button>
      </div>
    </div>
  );
};
